import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { RichTextDisplay } from '@bonusly/common-rich-text-editor';
import { Box, BoxProps, Button, DownloadOutline, Flex, IconButton, Image, rem, Text } from '@bonusly/design-system';

import { AnnouncementHeader } from '.';
import { useClaimIncentiveModal } from '../../../../modules/incentives';
import { Announcement } from '../../types/announcements';
import { stripEmojis } from '../../utils/text';
import { AnnouncementVideo } from '../announcement-video';

export interface AnnouncementCardProps extends BoxProps {
	announcement: Announcement;
	collapsed?: boolean;
	onClick?: () => void;
	fontSize?: 'sm' | 'lg';
}

export const AnnouncementCard: FC<AnnouncementCardProps> = ({ announcement, collapsed = false, onClick, fontSize = 'lg', ...boxProps }) => {
	const { t } = useTranslation('announcements');
	const { track } = useAnalytics();
	const claimIncentiveModal = useClaimIncentiveModal();

	const handleIncentiveClick = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			track(AnalyticsEvent.Announcements.AnnouncementClaimIncentiveButtonClicked);
			claimIncentiveModal.onOpen({ incentiveId: announcement.claimable_award_id });
		},
		[announcement, claimIncentiveModal, track]
	);

	const handleDownloadImage = useCallback(async () => {
		if (!announcement.image?.url) return;
		const filename = announcement.image.filename || 'announcement-image';
		try {
			const response = await fetch(announcement.image.url, { mode: 'cors' });
			const blob = await response.blob();
			const blobUrl = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(blobUrl);
		} catch {
			window.open(announcement.image.url, '_blank');
		}
	}, [announcement.image]);

	return (
		<Box
			as={collapsed ? 'button' : 'div'}
			aria-label={collapsed ? t('viewAnnouncement', { title: announcement.title }) : undefined}
			bg="bg.surface"
			p={collapsed ? 3 : 4}
			borderRadius="sm"
			mx="auto"
			width="100%"
			borderColor="bg.soft"
			textAlign="left"
			{...boxProps}
			onClick={onClick}
			cursor={collapsed ? 'pointer' : 'default'}
			data-testid="announcement-card"
		>
			{!collapsed && <AnnouncementHeader createdById={announcement.created_by_id} startDate={announcement.start_date} />}

			<Flex>
				<Box w="100%">
					<Text fontSize={fontSize === 'sm' ? 16 : 24} fontWeight="semibold">
						{collapsed ? stripEmojis(announcement.title) : announcement.title}
					</Text>

					{collapsed ? (
						<Box noOfLines={1} textOverflow="ellipsis" fontSize={14}>
							<RichTextDisplay>{announcement.description}</RichTextDisplay>
						</Box>
					) : (
						<Box fontSize={fontSize === 'sm' ? 14 : 16}>
							<RichTextDisplay>{announcement.description}</RichTextDisplay>
						</Box>
					)}

					{announcement.video && !collapsed && <AnnouncementVideo video={announcement.video} />}

					{!announcement.video && announcement.image?.url && announcement.full_width_image && !collapsed && (
						<Box mt={3} w="100%" position="relative">
							<Image
								alt={t('carousel.image_modal_image_alt')}
								borderRadius="sm"
								data-testid="announcement-image"
								src={announcement.image.url}
								w="100%"
								objectFit="cover"
								maxH={rem(400)}
							/>
							<IconButton
								aria-label={t('carousel.download_image')}
								data-testid="announcement-image-download"
								icon={<DownloadOutline />}
								onClick={handleDownloadImage}
								position="absolute"
								bottom={2}
								right={2}
								size="sm"
								variant="solid"
								colorScheme="blackAlpha"
								color="text.onColor"
								borderRadius="sm"
								opacity={0.75}
								_hover={{ opacity: 1 }}
							/>
						</Box>
					)}

					{announcement.claimable_award_id && (
						<>
							{collapsed ? (
								<Text as="span" color="text.interactive" fontSize="sm" fontWeight="medium">
									{t('carousel.incentive_button')}
								</Text>
							) : (
								<Button
									colorScheme="primary"
									data-testid="incentive-button"
									mt={4}
									onClick={handleIncentiveClick}
									size={fontSize === 'sm' ? 'xs' : 'sm'}
									variant="solid"
									_hover={{ textDecoration: 'none' }}
									maxW={rem(280)}
								>
									{t('carousel.incentive_button')}
								</Button>
							)}
						</>
					)}
				</Box>
				{!announcement.video && announcement.image?.url && (!announcement.full_width_image || collapsed) && (
					<Box w={rem(88)} ml={1} flexShrink={0}>
						<Image alt={t('carousel.image_modal_image_alt')} borderRadius="sm" data-testid="announcement-image" src={announcement.image.url} />
					</Box>
				)}
				{announcement.video && collapsed && (
					<Box
						position="relative"
						w={rem(88)}
						h={rem(64)}
						ml={1}
						flexShrink={0}
						borderRadius="sm"
						overflow="hidden"
						backgroundColor="bg.subtle"
						data-testid="announcement-video-thumbnail"
					>
						{(announcement.video.video_thumbnail_url || announcement.video.video_poster_url) && (
							<Image
								alt={t('carousel.video_modal_video_alt')}
								src={announcement.video.video_thumbnail_url || announcement.video.video_poster_url || undefined}
								position="absolute"
								inset={0}
								w="100%"
								h="100%"
								objectFit="cover"
							/>
						)}
					</Box>
				)}
			</Flex>
		</Box>
	);
};
